import {
  Box,
  Button,
  Container,
  Stack,
  styled,
  Typography,
  useTheme
} from "@mui/material";
import {
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2RowEmpty,
  ConfirmationModal,
  ErrorPanel,
  formatUtcDate,
  useToast
} from "@qubit/autoparts";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";
import { useDisclosure } from "~/hooks/useDisclosure";
import { extractUserNameFromAuth0Id } from "~/lib/helpers";

import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";

import { selectUserHasApiTokenAccessRole } from "~/redux/selectors/authSelectors";
import {
  useGetApiTokensQuery,
  useRevokeApiTokenMutation
} from "~/redux/warehouse/apiTokens.hooks";

import CreateApiTokenModal from "./CreateApiTokenModal";

const ApiTokensTableContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.gray.dark}`,
  borderRadius: "10px",
  m: 2,
  overflowX: "scroll",
  "&::-webkit-scrollbar": {
    display: "none"
  },
  scrollbarWidth: "none",
  msOverflowStyle: "none"
}));

type TokenToRevoke = {
  id: string;
  name: string;
};

/** Matches backend auth: expiration_date > CURRENT_DATE. Missing expiry never expires. */
function isApiTokenExpired(expiresOn?: Date | string | null): boolean {
  if (!expiresOn) return false;
  const expiryDate = dayjs.utc(expiresOn).startOf("day");
  if (!expiryDate.isValid()) return false;
  return !expiryDate.isAfter(dayjs.utc().startOf("day"));
}

const ApiTokensView = () => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { successToast, errorToast } = useToast();
  const { enableSideNavSubMenus } = useFlag();
  const [currentPage, setPage] = useState<number>(1);
  const [tokenToRevoke, setTokenToRevoke] = useState<TokenToRevoke | null>(
    null
  );
  const limit = 10;
  const userHasApiTokenAccessRole = useAppSelector(
    selectUserHasApiTokenAccessRole
  );
  const {
    open: isApiTokenCreationModalOpen,
    onOpen: onApiTokenCreationModalOpen,
    onClose: onApiTokenCreationModalClose
  } = useDisclosure();

  const [revokeApiToken, { isLoading: isRevokingApiToken }] =
    useRevokeApiTokenMutation();

  const {
    apiTokensData = [],
    error,
    areApiTokensFetching,
    refetch: refetchApiTokensList
  } = useGetApiTokensQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, error, isFetching }) => ({
      apiTokensData: data?.toSorted((a, b) => {
        const dateA = a.createdOn
          ? typeof a.createdOn === "string"
            ? new Date(a.createdOn).getTime()
            : a.createdOn.getTime()
          : null;
        const dateB = b.createdOn
          ? typeof b.createdOn === "string"
            ? new Date(b.createdOn).getTime()
            : b.createdOn.getTime()
          : null;
        return (
          (dateA && dateB && dateB - dateA) || a.name.localeCompare(b.name)
        );
      }),
      error,
      areApiTokensFetching: isFetching
    })
  });

  const totalPageCount = apiTokensData.length
    ? Math.ceil(apiTokensData.length / limit)
    : 0;
  const apiTokensToDisplay = useMemo(() => {
    return apiTokensData.slice((currentPage - 1) * limit, limit * currentPage);
  }, [apiTokensData, currentPage]);

  const handleCloseRevokeModal = () => {
    setTokenToRevoke(null);
  };

  const handleConfirmRevoke = async () => {
    if (!tokenToRevoke) return;
    try {
      await revokeApiToken({ tokenId: tokenToRevoke.id }).unwrap();
      successToast(t("api token revoke success"));
      handleCloseRevokeModal();
    } catch (err) {
      errorToast(getMessageFromRtkError(err));
    }
  };

  const panel = (
    <Stack spacing={4} p={2}>
      <Box>
        <Typography variant="h6">{t("organization api tokens")}</Typography>
        <Typography variant="subtitle1">
          {t("organization api tokens description")}
        </Typography>
      </Box>
      <Button
        color="primary"
        sx={{
          minWidth: 200,
          alignSelf: "flex-end"
        }}
        onClick={onApiTokenCreationModalOpen}
        disabled={!userHasApiTokenAccessRole}
      >
        {t("create api token")}
      </Button>
      {error && (
        <ErrorPanel
          variant="outline"
          message={getMessageFromRtkError(error)}
          sx={{
            backgroundColor: "error.light"
          }}
          tryAgain={{
            onTryAgain: async () => {
              await refetchApiTokensList();
            }
          }}
        />
      )}
      {!error && (
        <ApiTokensTableContainer>
          <ASTableV2
            gridTemplateColumns={`repeat(7, auto)`}
            isLoading={areApiTokensFetching}
            pagination={{
              pageCount: totalPageCount,
              page: currentPage,
              setPage: (pageNumber) => {
                setPage(pageNumber);
              }
            }}
          >
            <ASTableV2Header>
              <ASTableV2Row>
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("name")}</Typography>
                </ASTableV2Cell>
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("created by")}</Typography>
                </ASTableV2Cell>
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("roles")}</Typography>
                </ASTableV2Cell>
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("created on")}</Typography>
                </ASTableV2Cell>
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("expires on")}</Typography>
                </ASTableV2Cell>
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("status")}</Typography>
                </ASTableV2Cell>
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("action")}</Typography>
                </ASTableV2Cell>
              </ASTableV2Row>
            </ASTableV2Header>
            <ASTableV2Body>
              {!areApiTokensFetching && !apiTokensData.length && (
                <ASTableV2RowEmpty>
                  {t("no api tokens found")}
                </ASTableV2RowEmpty>
              )}
              {apiTokensToDisplay.map((apiToken, index) => {
                const {
                  id,
                  name,
                  userId,
                  expiresOn,
                  createdOn,
                  role,
                  enabled
                } = apiToken;
                const createdOnDate = formatUtcDate(createdOn) || "-";
                const expiresOnDate = formatUtcDate(expiresOn) || "-";
                const roles = role.join(", ");
                const expired = isApiTokenExpired(expiresOn);
                const canRevoke =
                  enabled && !expired && userHasApiTokenAccessRole;
                const statusLabel = !enabled
                  ? t("inactive")
                  : expired
                    ? t("expired")
                    : t("active");
                return (
                  <ASTableV2Row key={`${name}${index}-token`}>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">{name}</Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">
                        {extractUserNameFromAuth0Id(userId)}
                      </Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">{roles}</Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">
                        {createdOnDate}
                      </Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">
                        {expiresOnDate}
                      </Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">{statusLabel}</Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Button
                        color="error"
                        size="small"
                        disabled={!canRevoke}
                        onClick={() => setTokenToRevoke({ id, name })}
                      >
                        {t("revoke api token")}
                      </Button>
                    </ASTableV2Cell>
                  </ASTableV2Row>
                );
              })}
            </ASTableV2Body>
          </ASTableV2>
        </ApiTokensTableContainer>
      )}
      {isApiTokenCreationModalOpen && (
        <CreateApiTokenModal
          open={isApiTokenCreationModalOpen}
          onClose={onApiTokenCreationModalClose}
        />
      )}
      {tokenToRevoke && (
        <ConfirmationModal
          isOpen
          confirmCb={handleConfirmRevoke}
          closeCb={handleCloseRevokeModal}
          modalTitle={t("revoke api token")}
          modalText={`${t("are you sure you want to revoke the api token")} "${
            tokenToRevoke.name
          }"`}
          labelledById="revoke-api-token-modal-title"
          isConfirmLoading={isRevokingApiToken}
        />
      )}
    </Stack>
  );

  return enableSideNavSubMenus ? (
    <Container>
      <Box bgcolor={palette.background.secondary} pb="4px" mt={2}>
        <Box bgcolor={palette.background.default} mx="4px" borderRadius="8px">
          {panel}
        </Box>
      </Box>
    </Container>
  ) : (
    panel
  );
};

export default ApiTokensView;
